// Utilities for handling user mentions in forum content

/**
 * Detects @ mention triggers in text input
 * @param {string} text - The full text content
 * @param {number} cursorPosition - Current cursor position
 * @returns {Object} - Mention trigger information
 */
export const detectMentionTrigger = (text, cursorPosition) => {
  const beforeCursor = text.substring(0, cursorPosition);
  const mentionMatch = beforeCursor.match(/@(\w*)$/);
  
  if (mentionMatch) {
    return {
      isTriggered: true,
      searchTerm: mentionMatch[1],
      startPosition: cursorPosition - mentionMatch[0].length,
      triggerPosition: cursorPosition - mentionMatch[1].length
    };
  }
  
  return { isTriggered: false };
};

/**
 * Formats a mention for insertion into content
 * @param {Object} user - User object with id and displayName
 * @returns {string} - Formatted mention string
 */
export const formatMention = (user) => {
  return `@[${user.displayName}](${user.id})`;
};

/**
 * Parses mentions from content text with performance optimization
 * @param {string} content - Content with potential mentions
 * @returns {Array} - Array of mention objects
 */
export const parseMentions = (content) => {
  if (!content || typeof content !== 'string') {
    return [];
  }
  
  // Quick check to avoid regex if no mentions present
  if (!content.includes('@[')) {
    return [];
  }
  
  const mentionRegex = getCachedRegex('@\\[([^\\]]+)\\]\\(([^)]+)\\)');
  const mentions = [];
  let match;
  
  // Reset regex lastIndex for cached regex
  mentionRegex.lastIndex = 0;
  
  while ((match = mentionRegex.exec(content)) !== null) {
    mentions.push({
      displayName: match[1],
      userId: match[2],
      position: { 
        start: match.index, 
        end: match.index + match[0].length 
      },
      fullMatch: match[0]
    });
  }
  
  return mentions;
};

/**
 * Extracts unique user IDs from mentions
 * @param {Array} mentions - Array of mention objects
 * @returns {Array} - Array of unique user IDs
 */
export const extractMentionedUserIds = (mentions) => {
  const userIds = mentions.map(mention => mention.userId);
  return [...new Set(userIds)]; // Remove duplicates
};

/**
 * Validates mention format
 * @param {string} mentionText - The mention text to validate
 * @returns {boolean} - True if valid mention format
 */
export const isValidMention = (mentionText) => {
  const mentionRegex = /^@\[([^\]]+)\]\(([^)]+)\)$/;
  return mentionRegex.test(mentionText);
};

/**
 * Replaces mention text at cursor position
 * @param {string} text - Original text
 * @param {number} startPosition - Start position of mention trigger
 * @param {number} endPosition - End position (cursor)
 * @param {string} mentionText - Formatted mention to insert
 * @returns {Object} - New text and cursor position
 */
export const replaceMentionText = (text, startPosition, endPosition, mentionText) => {
  const beforeMention = text.substring(0, startPosition);
  const afterMention = text.substring(endPosition);
  const newText = beforeMention + mentionText + ' ' + afterMention;
  const newCursorPosition = startPosition + mentionText.length + 1;
  
  return {
    text: newText,
    cursorPosition: newCursorPosition
  };
};

/**
 * Generates content preview for notifications
 * @param {string} content - Full content text
 * @param {number} maxLength - Maximum preview length
 * @returns {string} - Truncated preview
 */
export const generateContentPreview = (content, maxLength = 100) => {
  // Remove HTML tags and get plain text
  const plainText = content.replace(/<[^>]*>/g, '').trim();
  
  if (plainText.length <= maxLength) {
    return plainText;
  }
  
  return plainText.substring(0, maxLength - 3) + '...';
};

/**
 * Sanitizes mention content to prevent XSS
 * @param {string} content - Content with mentions
 * @returns {string} - Sanitized content
 */
export const sanitizeMentionContent = (content) => {
  // Basic XSS prevention for mention content
  return content
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

/**
 * Converts mentions to display format for rendering
 * @param {string} content - Content with mention markup
 * @returns {string} - Content with HTML mention elements
 */
export const renderMentions = (content) => {
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  
  return content.replace(mentionRegex, (match, displayName, userId) => {
    const sanitizedName = sanitizeMentionContent(displayName);
    const sanitizedId = sanitizeMentionContent(userId);
    
    return `<span class="mention text-purple-600 dark:text-purple-400 underline cursor-pointer hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-sm px-0.5 -mx-0.5 transition-all duration-200" data-user-id="${sanitizedId}" data-mention="true">@${sanitizedName}</span>`;
  });
};

/**
 * Converts mentions to React components for rendering
 * @param {string} content - Content with mention markup
 * @param {Function} MentionComponent - React component to render mentions
 * @returns {Array} - Array of React elements and text nodes
 */
export const renderMentionsAsComponents = (content, MentionComponent) => {
  if (!content || typeof content !== 'string') {
    return [content];
  }
  
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = mentionRegex.exec(content)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      const textBefore = content.substring(lastIndex, match.index);
      if (textBefore) {
        parts.push(textBefore);
      }
    }
    
    // Add the mention component
    const displayName = match[1];
    const userId = match[2];
    
    parts.push({
      type: 'mention',
      userId,
      displayName,
      key: `mention-${userId}-${match.index}`
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after the last mention
  if (lastIndex < content.length) {
    const textAfter = content.substring(lastIndex);
    if (textAfter) {
      parts.push(textAfter);
    }
  }
  
  return parts;
};

/**
 * Process HTML content to replace mention spans with React components
 * @param {string} htmlContent - HTML content with mention spans
 * @returns {Array} - Array of processed content parts
 */
export const processMentionHTML = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return [htmlContent];
  }
  
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  const parts = [];
  
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent.trim()) {
        parts.push(node.textContent);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.classList.contains('mention') && node.dataset.userId) {
        parts.push({
          type: 'mention',
          userId: node.dataset.userId,
          displayName: node.textContent.replace('@', ''),
          key: `mention-${node.dataset.userId}-${parts.length}`
        });
      } else {
        // Process child nodes for other elements
        Array.from(node.childNodes).forEach(processNode);
      }
    }
  };
  
  Array.from(tempDiv.childNodes).forEach(processNode);
  
  return parts.length > 0 ? parts : [htmlContent];
};

/**
 * Convert HTML content with mention spans back to plain text with mention markup
 * @param {string} htmlContent - HTML content with mention spans
 * @returns {string} - Plain text with mention markup
 */
export const convertMentionSpansToMarkup = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent;
  }
  
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  // Convert mention spans back to markup format
  const mentionSpans = tempDiv.querySelectorAll('.mention-tag');
  mentionSpans.forEach(span => {
    const userId = span.getAttribute('data-user-id');
    const displayName = span.getAttribute('data-display-name');
    if (userId && displayName) {
      const mentionMarkup = `@[${displayName}](${userId})`;
      span.outerHTML = mentionMarkup;
    }
  });
  
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Convert mention markup to HTML with styled mention spans
 * @param {string} content - Content with mention markup
 * @returns {string} - HTML with styled mention spans
 */
export const convertMarkupToMentionSpans = (content) => {
  if (!content || typeof content !== 'string') {
    return content;
  }
  
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  
  return content.replace(mentionRegex, (match, displayName, userId) => {
    // Create a styled mention span that looks like the MentionRenderer component
    return `<span 
      class="mention-tag inline text-sm font-medium text-purple-600 dark:text-purple-400 underline decoration-purple-600 dark:decoration-purple-400 cursor-pointer transition-all duration-200 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-sm px-0.5 -mx-0.5" 
      data-user-id="${userId}" 
      data-display-name="${displayName}"
      data-mention="true"
      contenteditable="false"
    >@${displayName}</span>`;
  });
};

/**
 * Truncate content while preserving mention formatting
 * @param {string} content - Content with potential mentions
 * @param {number} maxLength - Maximum length of truncated content
 * @returns {string} - Truncated content with mentions preserved
 */
export const truncateContentWithMentions = (content, maxLength = 150) => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  // If content is shorter than max length, return as-is
  if (content.length <= maxLength) {
    return content;
  }

  // Parse mentions to identify their positions
  const mentions = parseMentions(content);
  
  // If no mentions, use simple truncation
  if (mentions.length === 0) {
    return content.substring(0, maxLength).trim() + '...';
  }

  // Find the best truncation point that doesn't break mentions
  let truncateAt = maxLength;
  
  // Check if truncation point falls within a mention
  for (const mention of mentions) {
    if (mention.position.start < maxLength && mention.position.end > maxLength) {
      // Truncation would break this mention, so truncate before it
      truncateAt = mention.position.start;
      break;
    }
  }

  // If we need to truncate before the first mention and it's too early, 
  // include the first mention completely
  if (truncateAt < maxLength * 0.5 && mentions.length > 0) {
    const firstMention = mentions[0];
    if (firstMention.position.end <= maxLength * 1.2) { // Allow 20% overflow for mentions
      truncateAt = firstMention.position.end;
    }
  }

  const truncated = content.substring(0, truncateAt).trim();
  return truncated + (truncateAt < content.length ? '...' : '');
};

/**
 * Get plain text content from content with mentions, preserving mention display names
 * @param {string} content - Content with potential mentions
 * @returns {string} - Plain text with mention names
 */
export const getTextContentWithMentions = (content) => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  // Replace mention markup with just the display name
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  return content.replace(mentionRegex, '@$1');
};

// Cache for compiled regex patterns
const regexCache = new Map();

/**
 * Get cached regex pattern
 * @param {string} pattern - Regex pattern string
 * @param {string} flags - Regex flags
 * @returns {RegExp} - Compiled regex
 */
const getCachedRegex = (pattern, flags = 'g') => {
  const key = `${pattern}:${flags}`;
  if (!regexCache.has(key)) {
    regexCache.set(key, new RegExp(pattern, flags));
  }
  return regexCache.get(key);
};

/**
 * Convert mention markup to HTML with editable mention spans for rich editor
 * @param {string} content - Content with mention markup
 * @returns {string} - HTML with styled editable mention spans
 */
export const convertMarkupToEditableHTML = (content) => {
  if (!content || typeof content !== 'string') {
    return content;
  }
  
  // Use cached regex for better performance
  const mentionRegex = getCachedRegex('@\\[([^\\]]+)\\]\\(([^)]+)\\)');
  
  return content.replace(mentionRegex, (match, displayName, userId) => {
    const sanitizedName = sanitizeMentionContent(displayName);
    const sanitizedId = sanitizeMentionContent(userId);
    
    return `<span 
      class="editable-mention inline text-sm font-medium text-purple-600 dark:text-purple-400 underline decoration-purple-600 dark:decoration-purple-400 cursor-pointer transition-all duration-200 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-sm px-0.5 -mx-0.5" 
      data-user-id="${sanitizedId}" 
      data-display-name="${sanitizedName}"
      data-mention="true"
      contenteditable="false"
      spellcheck="false"
    >@${sanitizedName}</span>`;
  });
};

/**
 * Convert HTML with editable mention spans back to mention markup
 * @param {string} htmlContent - HTML content with editable mention spans
 * @returns {string} - Plain text with mention markup
 */
export const convertEditableHTMLToMarkup = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent;
  }
  
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  // Convert editable mention spans back to markup format
  const mentionSpans = tempDiv.querySelectorAll('.editable-mention[data-mention="true"]');
  mentionSpans.forEach(span => {
    const userId = span.getAttribute('data-user-id');
    const displayName = span.getAttribute('data-display-name');
    if (userId && displayName) {
      const mentionMarkup = `@[${displayName}](${userId})`;
      span.outerHTML = mentionMarkup;
    }
  });
  
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Insert mention at cursor position in contentEditable element
 * @param {HTMLElement} editor - ContentEditable element
 * @param {Object} user - User object with id and displayName
 * @param {number} startPosition - Start position of @ trigger
 * @param {number} endPosition - End position (cursor)
 * @returns {void}
 */
export const insertMentionAtCursor = (editor, user, startPosition, endPosition) => {
  if (!editor || !user) return;
  
  const selection = window.getSelection();
  if (!selection.rangeCount) return;
  
  const range = selection.getRangeAt(0);
  
  // Create mention span element
  const mentionSpan = document.createElement('span');
  mentionSpan.className = 'editable-mention inline text-sm font-medium text-purple-600 dark:text-purple-400 underline decoration-purple-600 dark:decoration-purple-400 cursor-pointer transition-all duration-200 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-sm px-0.5 -mx-0.5';
  mentionSpan.setAttribute('data-user-id', user.id);
  mentionSpan.setAttribute('data-display-name', user.displayName);
  mentionSpan.setAttribute('data-mention', 'true');
  mentionSpan.setAttribute('contenteditable', 'false');
  mentionSpan.setAttribute('spellcheck', 'false');
  mentionSpan.textContent = `@${user.displayName}`;
  
  // Find and remove the @ trigger text
  const textContent = editor.textContent || '';
  const beforeTrigger = textContent.substring(0, startPosition);
  const afterCursor = textContent.substring(endPosition);
  
  // Clear editor content and rebuild with mention
  editor.innerHTML = '';
  
  // Add text before mention
  if (beforeTrigger) {
    const beforeNode = document.createTextNode(beforeTrigger);
    editor.appendChild(beforeNode);
  }
  
  // Add mention span
  editor.appendChild(mentionSpan);
  
  // Add space after mention
  const spaceNode = document.createTextNode(' ');
  editor.appendChild(spaceNode);
  
  // Add text after mention
  if (afterCursor) {
    const afterNode = document.createTextNode(afterCursor);
    editor.appendChild(afterNode);
  }
  
  // Set cursor position after the mention and space
  const newRange = document.createRange();
  newRange.setStartAfter(spaceNode);
  newRange.setEndAfter(spaceNode);
  selection.removeAllRanges();
  selection.addRange(newRange);
};

/**
 * Delete mention span when backspace/delete is pressed
 * @param {HTMLElement} spanElement - Mention span element to delete
 * @returns {void}
 */
export const deleteMentionSpan = (spanElement) => {
  if (!spanElement || !spanElement.parentNode) return;
  
  // Replace mention span with plain text
  const displayName = spanElement.getAttribute('data-display-name');
  const textNode = document.createTextNode(`@${displayName}`);
  spanElement.parentNode.replaceChild(textNode, spanElement);
};

/**
 * Preserve cursor position during content updates
 * @param {HTMLElement} editor - ContentEditable element
 * @param {Function} callback - Function to execute while preserving cursor
 * @returns {void}
 */
export const preserveCursorPosition = (editor, callback) => {
  if (!editor || typeof callback !== 'function') return;
  
  const selection = window.getSelection();
  let range = null;
  let cursorOffset = 0;
  
  // Save current cursor position
  if (selection.rangeCount > 0) {
    range = selection.getRangeAt(0);
    
    // Calculate offset from start of editor
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editor);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    cursorOffset = preCaretRange.toString().length;
  }
  
  // Execute callback
  callback();
  
  // Restore cursor position
  if (range && cursorOffset >= 0) {
    try {
      const textNodes = [];
      const walker = document.createTreeWalker(
        editor,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      let node;
      while (node = walker.nextNode()) {
        textNodes.push(node);
      }
      
      let currentOffset = 0;
      for (const textNode of textNodes) {
        const nodeLength = textNode.textContent.length;
        if (currentOffset + nodeLength >= cursorOffset) {
          const newRange = document.createRange();
          const offsetInNode = cursorOffset - currentOffset;
          newRange.setStart(textNode, Math.min(offsetInNode, nodeLength));
          newRange.setEnd(textNode, Math.min(offsetInNode, nodeLength));
          
          selection.removeAllRanges();
          selection.addRange(newRange);
          break;
        }
        currentOffset += nodeLength;
      }
    } catch (error) {
      console.debug('Error restoring cursor position:', error);
      // Fallback: place cursor at end
      const newRange = document.createRange();
      newRange.selectNodeContents(editor);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  }
};

/**
 * Get cursor position in contentEditable element
 * @param {HTMLElement} editor - ContentEditable element
 * @returns {number} - Cursor position offset
 */
export const getCursorPosition = (editor) => {
  if (!editor) return 0;
  
  const selection = window.getSelection();
  if (!selection.rangeCount) return 0;
  
  const range = selection.getRangeAt(0);
  let position = 0;
  
  try {
    const walker = document.createTreeWalker(
      editor,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          // Accept text nodes and mention spans
          if (node.nodeType === Node.TEXT_NODE) {
            return NodeFilter.FILTER_ACCEPT;
          }
          if (node.nodeType === Node.ELEMENT_NODE && 
              node.classList.contains('editable-mention')) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        }
      },
      false
    );
    
    let node;
    while (node = walker.nextNode()) {
      if (node.nodeType === Node.TEXT_NODE) {
        // Check if this text node contains the cursor
        if (node === range.startContainer) {
          position += range.startOffset;
          break;
        }
        // Check if cursor is before this node
        const compareResult = range.comparePoint(node, 0);
        if (compareResult === 1) {
          // Cursor is after this node, add its length
          position += node.textContent.length;
        } else {
          // Cursor is before this node, stop counting
          break;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE && 
                 node.classList.contains('editable-mention')) {
        // Check if cursor is before or after this mention
        const compareStart = range.comparePoint(node, 0);
        const compareEnd = range.comparePoint(node, node.childNodes.length);
        
        if (compareStart === 1) {
          // Cursor is after the mention start
          position += 1; // Mention counts as 1 character
        } else if (compareEnd === -1) {
          // Cursor is before the mention, stop counting
          break;
        }
      }
    }
  } catch (error) {
    console.debug('Error getting cursor position:', error);
    // Fallback to original method
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editor);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    position = preCaretRange.toString().length;
  }
  
  return position;
};

/**
 * Set cursor position in contentEditable element
 * @param {HTMLElement} editor - ContentEditable element
 * @param {number} position - Position to set cursor
 * @returns {void}
 */
export const setCursorPosition = (editor, position) => {
  if (!editor || position < 0) return;
  
  const selection = window.getSelection();
  const range = document.createRange();
  
  try {
    let currentPos = 0;
    const walker = document.createTreeWalker(
      editor,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          // Accept text nodes and mention spans
          if (node.nodeType === Node.TEXT_NODE) {
            return NodeFilter.FILTER_ACCEPT;
          }
          if (node.nodeType === Node.ELEMENT_NODE && 
              node.classList.contains('editable-mention')) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        }
      },
      false
    );
    
    let node;
    let foundPosition = false;
    
    while (node = walker.nextNode()) {
      if (node.nodeType === Node.TEXT_NODE) {
        const nodeLength = node.textContent.length;
        if (currentPos + nodeLength >= position) {
          const offsetInNode = position - currentPos;
          range.setStart(node, Math.min(offsetInNode, nodeLength));
          range.setEnd(node, Math.min(offsetInNode, nodeLength));
          foundPosition = true;
          break;
        }
        currentPos += nodeLength;
      } else if (node.nodeType === Node.ELEMENT_NODE && 
                 node.classList.contains('editable-mention')) {
        // Treat mention as a single character for positioning
        if (currentPos === position) {
          // Position cursor before the mention
          range.setStartBefore(node);
          range.setEndBefore(node);
          foundPosition = true;
          break;
        } else if (currentPos + 1 === position) {
          // Position cursor after the mention
          range.setStartAfter(node);
          range.setEndAfter(node);
          foundPosition = true;
          break;
        }
        currentPos += 1; // Mention counts as 1 character
      }
    }
    
    if (!foundPosition) {
      // Position not found, place cursor at end
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
  } catch (error) {
    console.debug('Error setting cursor position:', error);
    // Fallback: place cursor at end
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

/**
 * Check if cursor is inside a mention span
 * @param {HTMLElement} editor - ContentEditable element
 * @returns {HTMLElement|null} - Mention span element if cursor is inside one
 */
export const getCursorMentionSpan = (editor) => {
  if (!editor) return null;
  
  const selection = window.getSelection();
  if (!selection.rangeCount) return null;
  
  const range = selection.getRangeAt(0);
  let node = range.startContainer;
  
  // Traverse up the DOM tree to find mention span
  while (node && node !== editor) {
    if (node.nodeType === Node.ELEMENT_NODE && 
        node.classList.contains('editable-mention') &&
        node.getAttribute('data-mention') === 'true') {
      return node;
    }
    node = node.parentNode;
  }
  
  return null;
};

/**
 * Select entire mention span when cursor enters it
 * @param {HTMLElement} editor - ContentEditable element
 * @param {HTMLElement} mentionSpan - Mention span to select
 * @returns {void}
 */
export const selectMentionSpan = (editor, mentionSpan) => {
  if (!editor || !mentionSpan) return;
  
  const selection = window.getSelection();
  const range = document.createRange();
  
  try {
    range.selectNodeContents(mentionSpan);
    selection.removeAllRanges();
    selection.addRange(range);
  } catch (error) {
    console.debug('Error selecting mention span:', error);
  }
};

/**
 * Handle mention span deletion with proper cursor positioning
 * @param {HTMLElement} editor - ContentEditable element
 * @param {HTMLElement} mentionSpan - Mention span to delete
 * @param {boolean} deleteForward - Whether deletion is forward (Delete key) or backward (Backspace)
 * @returns {void}
 */
export const handleMentionDeletion = (editor, mentionSpan, deleteForward = false) => {
  if (!editor || !mentionSpan) return;
  
  const selection = window.getSelection();
  const range = document.createRange();
  
  try {
    // Position cursor before or after the mention based on deletion direction
    if (deleteForward) {
      range.setStartAfter(mentionSpan);
      range.setEndAfter(mentionSpan);
    } else {
      range.setStartBefore(mentionSpan);
      range.setEndBefore(mentionSpan);
    }
    
    // Remove the mention span
    mentionSpan.remove();
    
    // Set cursor position
    selection.removeAllRanges();
    selection.addRange(range);
  } catch (error) {
    console.debug('Error handling mention deletion:', error);
    // Fallback: just remove the mention
    mentionSpan.remove();
  }
};

/**
 * Check if selection spans across multiple mentions or partial mentions
 * @param {HTMLElement} editor - ContentEditable element
 * @returns {boolean} - True if selection is complex
 */
export const hasComplexSelection = (editor) => {
  if (!editor) return false;
  
  const selection = window.getSelection();
  if (!selection.rangeCount) return false;
  
  const range = selection.getRangeAt(0);
  
  // Check if selection spans multiple nodes
  if (range.startContainer !== range.endContainer) {
    return true;
  }
  
  // Check if selection is within a mention but not selecting the entire mention
  const mentionSpan = getCursorMentionSpan(editor);
  if (mentionSpan) {
    const mentionRange = document.createRange();
    mentionRange.selectNodeContents(mentionSpan);
    
    // Compare ranges to see if entire mention is selected
    return !(range.compareBoundaryPoints(Range.START_TO_START, mentionRange) === 0 &&
             range.compareBoundaryPoints(Range.END_TO_END, mentionRange) === 0);
  }
  
  return false;
};

/**
 * Handle click positioning in contentEditable with mention spans
 * @param {HTMLElement} editor - ContentEditable element
 * @param {MouseEvent} event - Click event
 * @returns {boolean} - True if positioning was handled, false to allow default behavior
 */
export const handleClickPositioning = (editor, event) => {
  if (!editor || !event) return false;
  
  try {
    const selection = window.getSelection();
    let clickedNode = event.target;
    
    // If clicked directly on a mention span
    if (clickedNode.classList && clickedNode.classList.contains('editable-mention')) {
      event.preventDefault();
      event.stopPropagation();
      
      const rect = clickedNode.getBoundingClientRect();
      const clickX = event.clientX;
      const mentionCenter = rect.left + (rect.width / 2);
      
      const range = document.createRange();
      
      if (clickX < mentionCenter) {
        // Clicked on left half, position cursor before mention
        range.setStartBefore(clickedNode);
        range.setEndBefore(clickedNode);
      } else {
        // Clicked on right half, position cursor after mention
        range.setStartAfter(clickedNode);
        range.setEndAfter(clickedNode);
      }
      
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Focus the editor to ensure it's active
      editor.focus();
      return true;
    }
    
    // If clicked inside a mention (on text content)
    const clickedElement = event.target.closest('.editable-mention');
    if (clickedElement) {
      event.preventDefault();
      event.stopPropagation();
      
      const range = document.createRange();
      // Always position cursor after the mention when clicking inside
      range.setStartAfter(clickedElement);
      range.setEndAfter(clickedElement);
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Focus the editor to ensure it's active
      editor.focus();
      return true;
    }
    
    // Check if we clicked in empty space after mentions
    const rect = editor.getBoundingClientRect();
    const clickX = event.clientX;
    const clickY = event.clientY;
    
    // If click is within editor bounds, use document.caretPositionFromPoint or document.caretRangeFromPoint
    if (clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom) {
      let range = null;
      
      if (document.caretPositionFromPoint) {
        const caretPos = document.caretPositionFromPoint(clickX, clickY);
        if (caretPos) {
          range = document.createRange();
          range.setStart(caretPos.offsetNode, caretPos.offset);
          range.setEnd(caretPos.offsetNode, caretPos.offset);
        }
      } else if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(clickX, clickY);
      }
      
      if (range) {
        // Check if the range would be inside a mention
        let node = range.startContainer;
        while (node && node !== editor) {
          if (node.nodeType === Node.ELEMENT_NODE && 
              node.classList.contains('editable-mention')) {
            // Position cursor after the mention instead
            event.preventDefault();
            event.stopPropagation();
            
            const newRange = document.createRange();
            newRange.setStartAfter(node);
            newRange.setEndAfter(node);
            selection.removeAllRanges();
            selection.addRange(newRange);
            
            editor.focus();
            return true;
          }
          node = node.parentNode;
        }
      }
    }
    
    // Allow default behavior for normal text areas
    return false;
  } catch (error) {
    console.debug('Error handling click positioning:', error);
    return false;
  }
};

/**
 * Clean up editor content by removing empty mentions and fixing structure
 * @param {HTMLElement} editor - ContentEditable element
 * @returns {void}
 */
export const cleanupEditorContent = (editor) => {
  if (!editor) return;
  
  // Remove empty mention spans
  const emptyMentions = editor.querySelectorAll('.editable-mention:empty');
  emptyMentions.forEach(span => span.remove());
  
  // Remove mention spans without proper attributes
  const invalidMentions = editor.querySelectorAll('.editable-mention:not([data-user-id]):not([data-display-name])');
  invalidMentions.forEach(span => {
    const textNode = document.createTextNode(span.textContent || '');
    span.parentNode?.replaceChild(textNode, span);
  });
  
  // Normalize whitespace
  const walker = document.createTreeWalker(
    editor,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );
  
  const textNodes = [];
  let node;
  while (node = walker.nextNode()) {
    textNodes.push(node);
  }
  
  textNodes.forEach(textNode => {
    // Collapse multiple spaces but preserve single spaces
    const normalizedText = textNode.textContent.replace(/\s+/g, ' ');
    if (normalizedText !== textNode.textContent) {
      textNode.textContent = normalizedText;
    }
  });
};

/**
 * Optimized debounce function for search input with immediate execution option
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Execute immediately on first call
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  let lastArgs;
  let lastThis;
  
  return function executedFunction(...args) {
    lastArgs = args;
    lastThis = this;
    
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(lastThis, lastArgs);
    };
    
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    
    if (callNow) func.apply(lastThis, lastArgs);
  };
};

/**
 * Throttle function for high-frequency events
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} - Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Memoization utility for expensive operations
 * @param {Function} fn - Function to memoize
 * @param {number} maxSize - Maximum cache size (default: 100)
 * @returns {Function} - Memoized function
 */
export const memoize = (fn, maxSize = 100) => {
  const cache = new Map();
  
  return function(...args) {
    const key = JSON.stringify(args);
    
    if (cache.has(key)) {
      return cache.get(key);
    }
    
    const result = fn.apply(this, args);
    
    // Implement LRU cache behavior
    if (cache.size >= maxSize) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    
    cache.set(key, result);
    return result;
  };
};